/**
 * OOH — Omniscient Orbital Harmony. Patent-pending IP brief + reusable badge.
 *
 * Three exports:
 *   • <OOHPatentBadge variant="pill" />     — a small "Patent Pending · OOH™" chip
 *   • <OOHBriefDialog open onClose />       — modal explaining what OOH is + how it works
 *   • <OOHBriefSection compact />           — inline block (for /trust + /modules pages)
 *
 * The brief stays in this single file so we have ONE canonical place to
 * edit the language. Every surface that mentions OOH should import from
 * here so the story stays consistent.
 */
import React, { useState } from 'react';
import { X, Sparkles, Shield, Telescope, GitBranch, ScrollText, Award } from 'lucide-react';

/* ─────────────────── canonical content ─────────────────── */

export const OOH_PILLARS = [
  {
    icon: Telescope,
    label: 'Multi-perspective by design',
    body: 'Seven sovereign AI agents — Problem-Solution Fit, Product-Market Fit, Scale, Risk, Capital, People, Sovereignty — orbit each venture. No single model can game the score because no single model sees the whole picture.',
  },
  {
    icon: Shield,
    label: 'Sovereign data isolation',
    body: 'Each orbital agent runs air-gapped inside your tenant. A leak in one satellite cannot compromise another. Telemetry never leaves the cluster.',
  },
  {
    icon: ScrollText,
    label: 'Evidence-gated milestones',
    body: 'Points only count when a documented artefact is on file. The OOH score is auditable end-to-end — every percentage point traces back to a timestamp, a file, and a clearer.',
  },
  {
    icon: GitBranch,
    label: 'Orthogonal, not sequential',
    body: 'Most frameworks (Maurya, Y Combinator) measure linear progress. OOH measures depth simultaneously — Asabiyyah, Sovereign Alignment, Capital Stewardship, and Founder Resilience all compound with — not after — your lean track.',
  },
];

export const OOH_BRIEF_HEAD =
  "OOH is Vigilant Entities' proprietary venture-grading model. Patent Pending · USPTO 63/PCT route.";
export const OOH_BRIEF_BODY =
  "Treat every startup as a small planet, orbited by seven satellites. Each satellite is independently scored by a dedicated AI agent. Yusuf — the council moderator — synthesises them into a single composite OOH Score (0–100) the holding company can defend in front of any investor or regulator.";

/* ─────────────────── badge ─────────────────── */

/**
 * <OOHPatentBadge />
 * variant = 'pill' (default) | 'inline' | 'large'
 * onClick optionally opens the brief.
 */
export function OOHPatentBadge({ variant = 'pill', onClick, className = '', label = 'Patent Pending · OOH™' }) {
  const base = 'inline-flex items-center gap-1 font-bold uppercase tracking-[0.2em] transition-colors';
  if (variant === 'large') {
    return (
      <button
        onClick={onClick}
        data-testid="ooh-badge-large"
        className={`${base} text-[10px] px-3 py-1.5 rounded-md border-2 border-[#8B3FC5] bg-[#FFF8E5] text-[#5E1F8B] hover:bg-[#FFEFC0] dark:bg-[#3a2f0a]/40 dark:text-[#D4AF37] ${className}`}
      >
        <Award size={11} /> {label}
      </button>
    );
  }
  if (variant === 'inline') {
    return (
      <span
        onClick={onClick}
        data-testid="ooh-badge-inline"
        className={`${base} text-[9px] text-[#5E1F8B] dark:text-[#D4AF37] cursor-pointer hover:underline ${className}`}
      >
        <Award size={9} /> {label}
      </span>
    );
  }
  return (
    <button
      onClick={onClick}
      data-testid="ooh-badge-pill"
      className={`${base} text-[9px] px-2 py-0.5 rounded-full border border-[#8B3FC5]/40 bg-[#FFF8E5]/70 text-[#5E1F8B] hover:border-[#8B3FC5] hover:bg-[#FFF8E5] dark:bg-[#3a2f0a]/40 dark:text-[#D4AF37] ${className}`}
    >
      <Award size={9} /> {label}
    </button>
  );
}

/* ─────────────────── inline section (for /trust + /modules) ─────────────────── */

export function OOHBriefSection({ compact = false }) {
  return (
    <section
      data-testid="ooh-brief-section"
      className={`relative overflow-hidden rounded-2xl border border-[#8B3FC5]/30 bg-gradient-to-br from-[#FFF8E5]/40 via-white to-[#F7F5F4] dark:from-[#3a2f0a]/40 dark:via-[#0d0d0d] dark:to-[#0A0A0A] ${compact ? 'p-6' : 'p-8 lg:p-10'}`}
    >
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#8B3FC5]/10 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[#5E1F8B] dark:text-[#D4AF37]">
          <Award size={11} /> Patent Pending · OOH™
        </div>
        <h2 className={`display font-bold mt-2 ${compact ? 'text-2xl' : 'text-3xl lg:text-4xl'}`}>
          Omniscient Orbital Harmony.
        </h2>
        <p className={`mt-3 text-slate-700 dark:text-white/75 ${compact ? 'text-sm max-w-2xl' : 'text-base max-w-3xl'}`}>
          {OOH_BRIEF_HEAD}
        </p>
        <p className={`mt-2 text-slate-600 dark:text-white/65 ${compact ? 'text-sm max-w-2xl' : 'text-base max-w-3xl'}`}>
          {OOH_BRIEF_BODY}
        </p>

        <div className={`mt-6 grid gap-4 ${compact ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-4'}`}>
          {OOH_PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.label}
                className="rounded-xl border border-slate-200 dark:border-white/10 bg-white/80 dark:bg-white/5 p-4 backdrop-blur-sm"
                data-testid={`ooh-pillar-${p.label.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <div className="h-8 w-8 rounded-md bg-[#FFF8E5] dark:bg-[#3a2f0a]/50 flex items-center justify-center text-[#5E1F8B] dark:text-[#D4AF37]">
                  <Icon size={15} />
                </div>
                <div className="mt-3 text-sm font-bold leading-snug">{p.label}</div>
                <p className="mt-1.5 text-xs text-slate-600 dark:text-white/60 leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>

        {!compact && (
          <p className="mt-6 text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-white/40 font-bold">
            OOH™ is a trademark of Vigilant Entities. Method patent pending — unauthorised reproduction prohibited.
          </p>
        )}
      </div>
    </section>
  );
}

/* ─────────────────── modal ─────────────────── */

export function OOHBriefDialog({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
      data-testid="ooh-brief-dialog"
    >
      <div
        className="relative max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-2xl bg-white dark:bg-[#0d0d0d] shadow-2xl border border-[#8B3FC5]/40"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close OOH brief"
          data-testid="ooh-brief-close"
          className="absolute top-4 right-4 h-9 w-9 rounded-full bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/20 flex items-center justify-center"
        >
          <X size={16} />
        </button>
        <div className="p-2 sm:p-4">
          <OOHBriefSection compact={false} />
        </div>
      </div>
    </div>
  );
}

/**
 * Convenience hook-less helper: a self-contained badge that opens the
 * brief on click. Use this when you want a one-liner inline.
 */
export function OOHBadgeWithBrief({ variant = 'pill', label, className }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <OOHPatentBadge variant={variant} label={label} className={className} onClick={() => setOpen(true)} />
      <OOHBriefDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export default OOHBadgeWithBrief;
